/**
 * _auth layout — centered card on the paper-alt stage.
 *
 * Stage 3 fills the card bodies (sign-in / sign-up / 2FA / reset
 * flows) on the low-level Clerk SDK. The layout itself hosts:
 *   - the brand mark (Logo) above the card so every auth surface
 *     has consistent identity;
 *   - a persistent footer with links to Terms and Privacy on the
 *     marketing site (`apps/www`, which owns the legal copy).
 *
 * The Terms/Privacy URLs point at `https://www.clickweave.co/{terms,privacy}`
 * — those pages don't exist yet; `apps/www` ships them separately.
 * The links here are the canonical surface users see; don't inline
 * legal text inside auth cards.
 */

import { Logo } from '@/components/shell/Logo';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
});

function AuthLayout(): ReactElement {
  return (
    <div className={'flex min-h-dvh w-full flex-col items-center justify-between p-6'}>
      <div aria-hidden="true" className="flex-1" />
      <div className="w-full max-w-sm">
        <div className="mb-5 flex items-center justify-center gap-2 text-[var(--color-accent-brand)]">
          <Logo size={28} />
          <span className="text-base font-semibold text-[var(--color-ink)]">ClickWeave</span>
        </div>
        <div
          className={
            'rounded-[var(--radius-panel)] border border-[var(--color-line)] ' +
            'bg-[var(--color-paper)] p-8 shadow-[var(--shadow-panel)]'
          }
        >
          <Outlet />
        </div>
      </div>
      <footer className="mt-6 flex flex-1 items-end justify-center gap-4 text-xs text-[var(--color-mute)]">
        <a
          href="https://www.clickweave.co/terms"
          target="_blank"
          rel="noreferrer"
          className="hover:text-[var(--color-ink)] hover:underline"
        >
          Terms
        </a>
        <a
          href="https://www.clickweave.co/privacy"
          target="_blank"
          rel="noreferrer"
          className="hover:text-[var(--color-ink)] hover:underline"
        >
          Privacy
        </a>
        <a
          href="https://www.clickweave.co"
          target="_blank"
          rel="noreferrer"
          className="hover:text-[var(--color-ink)] hover:underline"
        >
          clickweave.co
        </a>
      </footer>
    </div>
  );
}
